'use client'

import type { BaseTickContentProps } from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { RefreshCcw } from 'lucide-react'
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart } from 'recharts'

import type { ChartConfig } from '@ui/web/components'
import { Button, ChartContainer, Skeleton } from '@ui/web/components'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'

interface SkillRadarProps {
  childId: string
  className?: string
}

const MAX_LEVEL = 100

// Kept from earned-xp-bar: level curve is 500 XP/level for the first 5 levels, then 800 XP/level.
const getLevelFromXP = (xp: number) => {
  if (xp < 500) return 0

  const LEVELS_FIRST_PHASE = 5
  const XP_FIRST_PHASE = 500
  const XP_SECOND_PHASE = 800
  const xpToReachLevel5 = LEVELS_FIRST_PHASE * XP_FIRST_PHASE

  if (xp < xpToReachLevel5) {
    return Math.floor(xp / XP_FIRST_PHASE)
  }

  const remainingXP = xp - xpToReachLevel5
  const additionalLevels = Math.floor(remainingXP / XP_SECOND_PHASE)
  return Math.min(LEVELS_FIRST_PHASE + additionalLevels, MAX_LEVEL)
}

const chartConfig = {
  value: { label: 'XP', color: 'var(--chart-4)' },
} satisfies ChartConfig

interface RadarDatum {
  name: string
  xp: number
  level: number
  value: number
  iconUrl: string
}

const ICON_SIZE = 30
const ICON_RADIUS = 8
// Push each label/icon radially outward off the grid ring so they clear the polygon and grid.
const LABEL_RADIAL_GAP = 30
// Data polygon peaks at this fraction of the ring, leaving headroom between it and the labels.
const RADIUS_HEADROOM = 0.58

// Full SkillWeight names are too long to fit the radar's bottom vertices — show shorter display labels.
const SHORT_LABEL: Record<string, string> = {
  'Menulis & Beropini': 'Menulis',
  'Berpikir Matematis': 'Matematis',
}
const shortLabel = (name: string) => SHORT_LABEL[name] ?? name

export function SkillRadar({ childId, className }: SkillRadarProps) {
  const trpc = useSharedTRPC()

  // All-time XP: omit from/to so the procedure aggregates every EarnedXpDaily row for this child.
  const { data, isPending, isError, refetch } = useQuery(
    trpc.metrics.getCumulativeEarnedXp.queryOptions({ userId: childId }),
  )

  if (isError) {
    return (
      <div
        className={cn(
          'srd:flex srd:aspect-square srd:w-full srd:max-w-[360px] srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:mx-auto',
          className,
        )}
      >
        <p className='srd:text-sm srd:text-main-red-100'>Gagal memuat XP yang diperoleh</p>
        <Button variant='outline' size='sm' onClick={() => refetch()}>
          <RefreshCcw className='srd:size-4 srd:mr-2 srd:text-main-blue-100' />
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <Skeleton
        className={cn('srd:aspect-square srd:w-full srd:max-w-[360px] srd:rounded-full srd:mx-auto', className)}
      />
    )
  }

  const chartData: RadarDatum[] = data.map((v) => ({
    name: v.name,
    xp: v.cumulativeEarnedXp,
    level: getLevelFromXP(v.cumulativeEarnedXp),
    value: v.cumulativeEarnedXp,
    iconUrl: v.iconUrl,
  }))

  // Icon sits above the two-line label; the whole group is pushed radially outward off the ring so it clears the polygon.
  const renderTick = ({ payload, x, y, index }: BaseTickContentProps) => {
    const label = String(payload.value)
    const datum = chartData.find((d) => d.name === label)
    // recharts passes angle=0 for every tick; the real polar angle is payload.coordinate (degrees, 90=top, CCW).
    const rad = ((payload.coordinate ?? 0) * Math.PI) / 180
    const cx = Number(x) + Math.cos(rad) * LABEL_RADIAL_GAP
    const cy = Number(y) - Math.sin(rad) * LABEL_RADIAL_GAP
    const iconX = cx - ICON_SIZE / 2
    const iconY = cy - ICON_SIZE - 16
    const clipId = `skill-icon-clip-${index}`
    return (
      <g>
        {datum?.iconUrl ? (
          <>
            <clipPath id={clipId}>
              <rect x={iconX} y={iconY} width={ICON_SIZE} height={ICON_SIZE} rx={ICON_RADIUS} ry={ICON_RADIUS} />
            </clipPath>
            <image
              href={datum.iconUrl}
              x={iconX}
              y={iconY}
              width={ICON_SIZE}
              height={ICON_SIZE}
              clipPath={`url(#${clipId})`}
              preserveAspectRatio='xMidYMid slice'
            />
          </>
        ) : null}
        <text x={cx} y={cy} textAnchor='middle' className='srd:fill-neutrals-white'>
          <tspan x={cx} fontSize={12} fontWeight={600}>
            {shortLabel(label)}
          </tspan>
          <tspan x={cx} dy={16} fontSize={11} className='srd:fill-neutrals-white/80'>
            {datum ? `${Math.round(datum.xp).toLocaleString('id-ID')} / Lv. ${datum.level}` : ''}
          </tspan>
        </text>
      </g>
    )
  }

  const maxValue = Math.max(...chartData.map((d) => d.value), 1)
  const radiusDomainMax = maxValue / RADIUS_HEADROOM

  return (
    <ChartContainer
      config={chartConfig}
      className={cn('srd:mx-auto srd:lg:mx-0 srd:aspect-square srd:w-full srd:max-w-[360px]', className)}
    >
      <RadarChart data={chartData} outerRadius='50%' margin={{ top: 8, right: 14, bottom: 8, left: 14 }}>
        <PolarGrid className='srd:stroke-neutrals-white/25' />
        <PolarAngleAxis dataKey='name' tick={renderTick} />
        <PolarRadiusAxis domain={[0, radiusDomainMax]} tick={false} axisLine={false} />
        <Radar dataKey='value' fill='var(--chart-4)' fillOpacity={0.7} stroke='var(--chart-4)' strokeWidth={2} />
      </RadarChart>
    </ChartContainer>
  )
}
